import { Banner, Button, Dialog, DialogContent, DialogDescription, DialogTitle, Text } from '@psyomics/ui-components';
import { ChangeEvent, RefObject, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UploadService } from '../../types';
import { useUploadReferralCsv } from '../../use-upload-referral-csv';

type UploadCsvFormProps = {
  readonly service: UploadService;
  readonly onUploadAcknowledged: () => void;
};

type CsvFileChooserProps = {
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly selectedFile: File | null;
};

const CsvFileChooser = ({ inputRef, onChange, selectedFile }: CsvFileChooserProps) => {
  const { t } = useTranslation();
  const selectedFileNameId = useId();

  return (
    <>
      <input accept=".csv,text/csv" className="hidden" onChange={onChange} ref={inputRef} type="file" />
      <Text id={selectedFileNameId} role="status" size="sm">
        {selectedFile?.name ?? t('page.referralUploadJobs.noFileChosen')}
      </Text>
      <Button
        appearance="secondary"
        aria-describedby={selectedFileNameId}
        onClick={() => inputRef.current?.click()}
        size="sm"
      >
        {t('page.referralUploadJobs.chooseFile')}
      </Button>
    </>
  );
};

const UploadCsvForm = ({ service, onUploadAcknowledged }: UploadCsvFormProps) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { clearResult, errorMessage, result, uploadFile, uploading } = useUploadReferralCsv(service);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearResult();
    setSelectedFile(event.target.files?.[0] ?? null);
  };

  const handleUpload = async (file: File) => {
    const { success } = await uploadFile(file);
    if (!success) return;

    setSelectedFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="flex flex-col gap-3 py-4">
      <div className="flex items-center gap-3">
        <CsvFileChooser inputRef={inputRef} onChange={handleFileChange} selectedFile={selectedFile} />
        <Button
          disabled={!selectedFile}
          loading={uploading}
          onClick={() => handleUpload(selectedFile as File)}
          size="sm"
        >
          {t('page.referralUploadJobs.upload')}
        </Button>
      </div>
      <Dialog onOpenChange={(open) => !open && onUploadAcknowledged()} open={result !== null}>
        <DialogContent showCloseButton={false}>
          <DialogTitle>{t('page.referralUploadJobs.uploadSuccessTitle')}</DialogTitle>
          <DialogDescription asChild>
            <Text size="sm">
              {result &&
                t(
                  result.rowCount === 1
                    ? 'page.referralUploadJobs.uploadSuccessOne'
                    : 'page.referralUploadJobs.uploadSuccessOther',
                  { rowCount: result.rowCount }
                )}
            </Text>
          </DialogDescription>
          <div className="flex justify-end">
            <Button onClick={onUploadAcknowledged} size="sm">
              {t('common.buttons.ok')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {errorMessage && <Banner variant="error">{errorMessage}</Banner>}
    </div>
  );
};

export { UploadCsvForm };
